package nl.volleyplanner.web.controller

import kotlinx.browser.document
import kotlinx.coroutines.async
import kotlinx.coroutines.flow.*
import kotlinx.html.*
import kotlinx.html.dom.create
import nl.volleyplanner.api.GroupType
import nl.volleyplanner.api.PersonInclude
import nl.volleyplanner.api.auth.AuthLevel
import nl.volleyplanner.api.entities.FullPerson
import nl.volleyplanner.api.entities.Group
import nl.volleyplanner.api.entities.VolunteerJob
import nl.volleyplanner.sportconfig.NevoboAgeGroup
import nl.volleyplanner.sportconfig.NevoboTeamName
import nl.volleyplanner.web.CoroutineManager.RENDER_SCOPE
import nl.volleyplanner.web.VolleyPlannerApplication
import nl.volleyplanner.web.base.*
import nl.volleyplanner.web.base.WebPageController
import nl.volleyplanner.web.component.SeasonSelectionComponent
import nl.volleyplanner.web.controller.members.PersonEditModal
import nl.volleyplanner.web.controller.members.PersonRecordLine
import nl.volleyplanner.web.controller.planner.MemberImportController
import nl.volleyplanner.web.launchAsync
import nl.volleyplanner.web.lib.ScrollSpy
import nl.volleyplanner.web.util.jsObject
import org.w3c.dom.HTMLElement


class LedenController(override val parent: MemberImportController) : StaticPageController() {
    override val name = "LedenController"
    override val title = "Leden"
    override val path = "/overzicht"
    override val requiredAuthLevel = AuthLevel.AdminUser
    override val childPages: List<WebPageController> = emptyList()

    val availableTeamAndRefereeGroups = MutableStateFlow<Pair<List<Group>, List<Group>>?>(null)
    val personState = MutableStateFlow<List<FullPerson>?>(null)

    val seasonSelectionComponent = SeasonSelectionComponent()

    override fun createRuntimeInstance() = LedenController(parent)

        data class PersonRecord(
        val person: FullPerson,
        val team: Group?,
        val refereeGroups: List<Group>,
        val volunteerJobs: List<VolunteerJob>
    )

    inner class PersonAndGroupComponent : HTMLComponent() {
        private val seasonListenJob = launchAsync(RENDER_SCOPE) {
                seasonSelectionComponent.selectedSeasonFlow.collect {
                    refreshData()
                }
            }

        override fun onReady() {
            super.onReady()
            refreshData()
        }

        override fun onDispose() {
            seasonListenJob.cancel()
        }

        fun refreshData() {
            launchAsync(RENDER_SCOPE) {
                personState.value = null
                VolleyPlannerApplication.volleyplannerApiClient
                    .organisation
                    .persons
                    .getPersons(
                        include = setOf(PersonInclude.Groups, PersonInclude.VolunteerJobs),
                        groups = listOf(GroupType.Scheidsrechters, GroupType.Team),
                        season = seasonSelectionComponent.selectedSeason.await()
                    )
            }.onComplete {
                personState.value = it
            }

            launchAsync(RENDER_SCOPE) {
                availableTeamAndRefereeGroups.value = null
                Pair(
                VolleyPlannerApplication.volleyplannerApiClient
                    .organisation
                    .groups
                    .getGroupsByType(seasonSelectionComponent.selectedSeason.await(), GroupType.Team),
                    VolleyPlannerApplication.volleyplannerApiClient
                        .organisation
                        .groups
                        .getGroupsByType(seasonSelectionComponent.selectedSeason.await(), GroupType.Scheidsrechters)
                )
            }.onComplete {
                availableTeamAndRefereeGroups.value = Pair(it.first.groups, it.second.groups)
            }
        }

        override fun onContentReplaced(htmlElement: HTMLElement) {
            super.onContentReplaced(htmlElement)
            val sectionsElement = document.getElementById("vp-team-sections")
            if (sectionsElement != null) {
                ScrollSpy(sectionsElement, jsObject {
                    it.target = "#vp-team-nav"
                    it.rootMargin = "0px 0px -50% 0px"
                })
            }
        }

        override fun render(consumer: TagConsumer<HTMLElement>): HTMLElement {
            val personEditModalPromise = componentScope.async {
                createStandaloneComponent(
                    PersonEditModal(seasonSelectionComponent.selectedSeasonFlow, availableTeamAndRefereeGroups.asStateFlow(), { refreshData() })
                )
            }

            return consumer.div {
                if (
                    personState.value == null ||
                    seasonSelectionComponent.selectedSeasonFlow.value == null ||
                    availableTeamAndRefereeGroups.value == null
                    ) {
                    spinner()
                }
                else {
                    val persons = personState.value!!

                    // Build a flat list of person records, duplicating per team; people without a team get one record with team = null
                    val records = persons.flatMap { person ->
                        val teams = person.groups?.get(GroupType.Team.paramValue).orEmpty()
                        val refereeGroups = person.groups?.get(GroupType.Scheidsrechters.paramValue).orEmpty()
                        val volunteerJobs = person.volunteerJobs.orEmpty()
                        if (teams.isEmpty()) listOf(PersonRecord(person, null, refereeGroups, volunteerJobs))
                        else teams.map { team -> PersonRecord(person, team, refereeGroups, volunteerJobs) }
                    }
                    val recordsByTeamGroupId = records.groupBy { it.team?.id }
                    val teamGroups = records.map { it.team }.toSet()
                        .sortedBy { it?.name ?: "Z" }

                    // Determine distinct teams and order (teams first A-Z, then "Geen team")
                    val allTeamGroups = availableTeamAndRefereeGroups.value!!.first
                    val teamSections = (allTeamGroups + null)
                        .associateWith { recordsByTeamGroupId[it?.id] ?: emptyList() }
                        .toList()
                        .sortedWith(compareBy<Pair<Group?, List<PersonRecord>>>(
                            { it.first == null },
                            { it.first?.name?.let { name -> NevoboTeamName.parse(name).teamIdentifier} }
                        ))

                    component(seasonSelectionComponent)

                    nav (classes = "navbar sticky-top bg-body-tertiary px-2 py-0 mb-3 border") {
                        id = "vp-team-nav"
                        val seniorTeams = teamSections
                            .filter { it.first != null }
                            .filter { NevoboTeamName.parse(it.first!!.name).ageGroup == NevoboAgeGroup.Senior }
                            .map { it.first!! }
                        if (seniorTeams.isNotEmpty()) {
                            nav (classes = "navbar py-0") {
                                a(classes = "navbar-brand") { href = "#"; +"Senioren" }
                                ul (classes = "nav nav-pills") {
                                    seniorTeams.forEach { group ->
                                        a (classes = "nav-link") {
                                            href = "#vp-team-section-${group.id.toCssSelector()}"
                                            +NevoboTeamName.parse(group.name).teamIdentifier
                                        }
                                    }
                                }
                            }
                        }
                        val jeugdTeams = teamSections
                            .filter { it.first != null }
                            .filter { NevoboTeamName.parse(it.first!!.name).ageGroup !in setOf(NevoboAgeGroup.Senior,
                                NevoboAgeGroup.CMV) }
                            .map { it.first!! }
                        if (jeugdTeams.isNotEmpty()) {
                            nav (classes = "navbar py-0") {
                                a(classes = "navbar-brand") { href = "#"; +"Jeugd" }
                                ul (classes = "nav nav-pills") {
                                    jeugdTeams.forEach { group ->
                                        a (classes = "nav-link") {
                                            href = "#vp-team-section-${group.id.toCssSelector()}"
                                            +NevoboTeamName.parse(group.name).teamIdentifier
                                        }
                                    }
                                }
                            }
                        }
                        val cmvTeams = teamSections
                            .filter { it.first != null }
                            .filter { NevoboTeamName.parse(it.first!!.name).ageGroup == NevoboAgeGroup.CMV }
                            .map { it.first!! }
                        if (cmvTeams.isNotEmpty()) {
                            nav (classes = "navbar py-0") {
                                a(classes = "navbar-brand") { href = "#"; +"CMV" }
                                ul (classes = "nav nav-pills") {
                                    cmvTeams.forEach { group ->
                                        a (classes = "nav-link") {
                                            href = "#vp-team-section-${group.id.toCssSelector()}"
                                            +NevoboTeamName.parse(group.name).teamIdentifier
                                        }
                                    }
                                }
                            }
                        }
                    }
                    div {
                        id = "vp-team-sections"
                        style = "scroll-margin-top: 100px;"

                        attributes["data-bs-spy"] = "scroll"
                        attributes["data-bs-target"] = "#vp-team-nav"

                        // Render sections per team with header and table
                        teamSections.forEach { (group, teamRecords) ->
                            div(classes = "vp-team-section") {
                                attributes["data-group-id"] = group?.id ?: "other"
                                id = "vp-team-section-${(group?.id ?: "other").toCssSelector()}"

                                h6(classes = "mt-3 mb-1") { +(group?.name ?: "Geen team") }

                                // Header row for this team
                                div(classes = "row mx-0 py-2 border-bottom bg-light") {
                                    div(classes = "col-4 ps-3") { strong { +"Naam" } }
                                    div(classes = "col-3") { strong { +"Team" } }
                                    div(classes = "col-2") { strong { +"Scheids niveau" } }
                                    div(classes = "col-3") { strong { +"Vrijwilligers taken" } }
                                }

                                // Data rows for this team
                                val sortedTeam = teamRecords.sortedBy { it.person.name }
                                sortedTeam.forEach { record ->
                                    component(PersonRecordLine(record, personEditModalPromise, seasonSelectionComponent.selectedSeasonFlow.value!!.active))
                                }
                            }
                        }
                    }
                }
            }
        }

        override suspend fun getState() = personState.combine(seasonSelectionComponent.selectedSeasonFlow, ::Pair)
            .combine(availableTeamAndRefereeGroups, ::Pair)
            .stateIn(RENDER_SCOPE, SharingStarted.Eagerly, Pair(
                Pair(personState.value, seasonSelectionComponent.selectedSeasonFlow.value),
                availableTeamAndRefereeGroups.value
            ))
    }

    override suspend fun renderContent(): HTMLElement {
        val personAndGroupComponent = PersonAndGroupComponent()
        return document.create.div(classes = "container") {
            h2 { +"Leden" }
            component(PersonAndGroupComponent())
        }
    }
}